import type { Language } from '@eformulieren/engine'
import type { Finding, Field } from '@eformulieren/model'
import { type ReactNode, useCallback, useId, useState } from 'react'
import { WarningIcon } from '../icons/WarningIcon.js'
import { inLanguage } from '../language/text.js'
import { word } from '../language/words.js'
import { RequiredMark } from './RequiredMark.js'

/**
 * De schil om één veld: label, hulptekst, verplicht-aanduiding en meldingen.
 *
 * **Hulptekst staat permanent bij het veld en nooit achter een tooltip** (R-09,
 * R-10). Dat is niet alleen een voorkeur: een tooltip is met toetsenbord en
 * schermlezer slecht bereikbaar (R-139).
 *
 * **De verplicht-aanduiding komt uit de regels en niet uit een vlag op het veld**
 * (R-26, ontwerp 6.2). De schil krijgt haar als prop; de afleiding zelf staat in
 * `requiredFields` van de engine, zodat elke afnemer dezelfde krijgt.
 *
 * **Een rood sterretje achter het label van wat verplicht is** (besluit van 1 november 2026,
 * ontwerp 10.3, R-09). Dat vervangt "(optioneel)" bij wat niet hoeft (besluit van 29 oktober 2026):
 * de gebruiker zag niet wat verplicht was, en het sterretje is het teken dat een invuller kent.
 * Bovenaan het formulier staat één zin die het uitlegt (`Form.tsx`). Het sterretje is
 * `aria-hidden`, zodat een schermlezer geen "ster" in de naam hoort; hij hoort "verplicht" via
 * `aria-required`, en dat blijft bij elk verplicht veld staan, ook waar het sterretje niet staat.
 *
 * **Een harde melding komt bij het verlaten van het veld, niet bij elke
 * toetsaanslag** (R-25). De voorwaardenstatus loopt wel live mee (R-24): de
 * evaluator rekent bij elke wijziging door, dus de sectie- en formulierstand
 * kloppen altijd; alleen de melding onder dit veld wacht tot de invuller het veld
 * verlaat. Anders staat er "dit veld is verplicht" terwijl iemand de eerste letter
 * typt.
 *
 * **Waarschuwingen wachten niet.** Ze blokkeren niets (R-23, R-31) en zijn advies;
 * ze verschijnen zodra ze gelden.
 */

export interface FieldShellProps {
  readonly veld: Field
  /** Het nummer binnen de sectie, of niets als dit veld geen vraag is (R-04). */
  readonly nummer: number | undefined
  readonly taal: Language
  /** Of er een harde `verplicht`-regel op dit veld staat (R-09, uit de engine). */
  readonly verplicht: boolean
  /**
   * Of dit veld het sterretje mag krijgen: een invoerveld dat deze lezer mag bewerken (besluit van 1
   * november 2026, R-09). Van de aanroeper, want alleen die weet of de sectie bewerkbaar is en of de
   * control invoer vraagt (`takesInput` in de registry). Het sterretje staat er alleen als het veld
   * ook `verplicht` is; een verwijzing, een alleen-lezen veld of een veld van een ander krijgt het niet.
   */
  readonly sterretje?: boolean
  readonly hardeControles: readonly Finding[]
  readonly waarschuwingen: readonly Finding[]
  /**
   * Meldingen tonen zonder dat de invuller het veld heeft aangeraakt. Zet dit bij
   * een indienpoging: dan moet alles zichtbaar zijn wat indienen tegenhoudt
   * (R-24), ook in velden waar niemand is geweest.
   */
  readonly showAllNotices?: boolean
  /**
   * Of de kop van dit veld via `aria-labelledby` aan de control hangt in plaats van
   * via een `label for`.
   *
   * Dat is nodig zodra er geen enkel labelbaar element is om naar te wijzen: een
   * groep radioknoppen of aanvinkregels, een schaal, en de bewerkbare laag van een
   * rich-text-veld (een `contenteditable`). Naar één van de opties wijzen zou juist
   * die optie anders laten voorlezen dan de rest.
   *
   * De aanroeper weet dit uit `labelViaHeader` bij de registry, zodat de kennis over de
   * controls op één plek staat en niet in de schil.
   */
  readonly labelViaHeader?: boolean
  /**
   * Of het veld leeg is (besluit van 30 oktober 2026, R-25). Een leeg verplicht veld meldt zich
   * niet bij het verlaten, maar pas bij "Controleren en indienen" (`showAllNotices`): wie met Tab
   * door een concept loopt dat weken duurt, liet anders een spoor van rode meldingen achter. Op een
   * leeg veld falen alleen de regels die op leeg falen (`requiredChecks` in de engine); een
   * melding over wat er wél staat (formaat, lengte, bereik) blijft bij het verlaten komen (R-25,
   * R-01f).
   */
  readonly empty?: boolean
  /**
   * Het label staat in de control zelf, naast het vinkje van een akkoord (besluit van 30 oktober
   * 2026, R-89). De schil tekent dan geen zichtbare kop; de control draagt de naam.
   */
  readonly labelInControl?: boolean
  /**
   * Id's van elementen buiten deze schil die dit veld mee beschrijven, zoals de markeringen die
   * `SectionPanel` naast het veld tekent (voorvuld, alleen-intern, gewijzigd). Zonder dit kanaal
   * staan die markeringen alleen in de visuele leesorde naast het veld en nooit in zijn
   * `aria-describedby`: wie met het toetsenbord of een schermlezer van veld naar veld springt
   * hoort de markering dan nooit (WCAG 1.3.1, M8.12).
   */
  readonly extraDescription?: readonly string[]
  /**
   * Een vast id op de schil, waar het overzicht van openstaande controles naartoe linkt (R-140).
   *
   * Het id van de control zelf komt uit `useId()` en is dus van buiten niet te kennen; dit anker
   * wel, want de aanroeper kiest het. De link wijst naar de schil en niet naar de control, omdat
   * niet elke control focus kan krijgen (een radiogroep, een herhaalgroep): wie springt, zoekt
   * binnen de schil het element dat de focus wel kan nemen (`focusInField`).
   */
  readonly anchor?: string
  /**
   * Of deze lezer het antwoord alleen leest: een invoerveld dat hij niet kan bewerken (opdracht van
   * 30 oktober 2026, ontwerp `Toets.dc.html`, R-133). De hulptekst zegt hoe je een vraag invult en
   * is voor wie leest ruis, dus die valt weg (R-10 gaat over wie invult: daar blijft hij permanent
   * staan). Is het antwoord ook leeg, dan staat er één gedempte regel "Niet ingevuld" in plaats van
   * een leeg vak of een datumsjabloon, zodat leeg niet op een kapotte weergave lijkt.
   */
  readonly reading?: boolean
  /**
   * Wie er nu in dit veld werkt (`FieldPresence`, R-72), op de bovenrand van de control. Krijgt het id
   * mee waar de control met `aria-describedby` naar wijst, zodat wie met de Tab-toets binnenkomt hoort
   * wie hier al werkt. Alleen bij een verbinding; zonder staat er niets en schuift er niets.
   */
  readonly presence?: (id: string) => ReactNode
  /**
   * De control. Krijgt de aria-koppelingen aangereikt in plaats van ze zelf te
   * verzinnen: zo staat de koppeling tussen veld, hulptekst en melding op één
   * plek, en is R-140 niet afhankelijk van of elke control het goed doet.
   */
  readonly children: (link: FieldLink) => ReactNode
}

export interface FieldLink {
  readonly id: string
  readonly 'aria-describedby': string | undefined
  readonly 'aria-invalid': boolean | undefined
  readonly 'aria-required': boolean | undefined
  /**
   * Alleen gezet bij een groepscontrol: het id van de kop, waar de groep zich met
   * `aria-labelledby` aan hangt. Een `label for` kan niet naar een `fieldset`
   * wijzen, en naar één van de opties wijzen maakt juist die optie anders dan de
   * rest: die zou als "vraag plus optie" worden voorgelezen en de andere alleen als
   * optie.
   */
  readonly 'aria-labelledby': string | undefined
}

export function FieldShell({
  veld: field,
  nummer: sectionNumber,
  taal: language,
  verplicht: required,
  sterretje: markable = false,
  hardeControles: hardChecks,
  waarschuwingen: warnings,
  showAllNotices = false,
  labelViaHeader = false,
  empty = false,
  labelInControl = false,
  extraDescription = [],
  anchor,
  reading = false,
  presence,
  children,
}: FieldShellProps): ReactNode {
  const [touched, setTouched] = useState(false)
  // React bindt onBlur aan focusout, en die bubbelt: de schil merkt het verlaten
  // van elk invoerelement erin, ook bij een samengestelde control zoals een
  // keuzelijst met chips (R-15).
  const handleLeave = useCallback(() => setTouched(true), [])

  // Een geplaatst component brengt zijn eigen presentatie mee (R-46) en heeft
  // daarom geen verplicht label en geen hulptekst; de schil moet dat aankunnen
  // zonder een leeg kopje te tekenen.
  const helpText = reading ? undefined : 'hulptekst' in field ? field.hulptekst : undefined
  // Leeg en alleen te lezen: geen control, dus ook geen element waar een `label for` naar wijst.
  const readEmpty = reading && empty
  const label = field.label

  const own = useId()
  const fieldId = `${own}-${field.id}`
  const headerId = `${fieldId}-kop`
  const helpId = `${fieldId}-hulp`
  const errorId = `${fieldId}-fout`
  const warningId = `${fieldId}-waarschuwing`
  const presenceId = `${fieldId}-aanwezig`

  // Bij het verlaten alleen wat niet op een leeg veld wacht; bij de controle alles.
  const shown = showAllNotices || !empty ? hardChecks : []
  const showErrors = (touched || showAllNotices) && shown.length > 0
  const description = [
    helpText === undefined ? undefined : helpId,
    showErrors ? errorId : undefined,
    warnings.length > 0 ? warningId : undefined,
    presence === undefined || readEmpty ? undefined : presenceId,
    ...extraDescription,
  ]
    .filter((part): part is string => part !== undefined)
    .join(' ')

  const notices = (
    <ul className="ef-veld__meldingen" id={errorId}>
      {shown.map((finding) => (
        <li key={finding.regelId} className="ef-veld__melding ef-veld__melding--hard">
          <WarningIcon aria-hidden="true" />
          {inLanguage(finding.melding, language)}
        </li>
      ))}
    </ul>
  )

  return (
    <div
      className="ef-veld"
      id={anchor}
      data-control={field.control}
      data-veld={field.id}
      data-status={showErrors ? 'fout' : 'geldig'}
      onBlur={handleLeave}
    >
      {(() => {
        const content = (
          <>
            {sectionNumber === undefined ? null : <span className="ef-veld__nummer">{sectionNumber}.</span>}
            <span className="ef-veld__labeltekst">
              {label === undefined ? field.control : inLanguage(label, language)}
            </span>
            {/* Bij een akkoord staat het naast de verklaring in de control (`Toggle.tsx`), niet hier. */}
            {markable && required && !labelInControl ? <RequiredMark /> : null}
          </>
        )
        // Leeg en alleen te lezen: dan staat er geen vinkje met de tekst ernaast, dus de vraag hoort
        // zichtbaar boven "Niet ingevuld" te staan, ook bij een akkoord.
        if (labelInControl && !readEmpty) {
          // Geen zichtbare kop: het label staat naast het vinkje. Voor een schermlezer ook niet,
          // anders hoort hij de verklaring twee keer.
          return (
            <span className="ef-veld__label ef-sr-only" id={headerId} aria-hidden="true">
              {content}
            </span>
          )
        }
        return labelViaHeader || readEmpty ? (
          <span className="ef-veld__label" id={headerId}>
            {content}
          </span>
        ) : (
          <label className="ef-veld__label" htmlFor={fieldId}>
            {content}
          </label>
        )
      })()}

      {helpText === undefined ? null : (
        <p className="ef-veld__hulptekst" id={helpId}>
          {inLanguage(helpText, language)}
        </p>
      )}

      {/*
        Tussen label en hulptekst en de invoer, en niet eronder (audit R-28): wie het veld
        terugzoekt leest eerst wat er mis is, en de melding schuift niet weg achter een teller.

        Een polite live region: de melding verschijnt nadat de focus het veld heeft
        verlaten, dus zonder live region hoort een schermlezergebruiker hem niet en
        moet hij eerst terugkeren naar het veld. Polite en niet assertive, want het
        onderbreekt niets wat de invuller op dat moment doet.

        Alleen voor dat pad. Een melding die er staat omdat de invuller vroeg wat er nog
        openstaat (`toonAlleMeldingen`) en niet omdat hij het veld verliet, staat ernaast
        buiten de live region: die knop zet tientallen velden tegelijk aan, en in een live
        region zou een schermlezer ze allemaal voorlezen achter de kop van het overzicht die
        net de focus kreeg. Twee containers en niet één attribuut dat omslaat, want een
        `aria-live` die in dezelfde render verandert als de inhoud wordt onvoorspelbaar
        voorgelezen. Verlaat de invuller zo'n veld daarna, dan verhuist de melding naar de
        live region en klinkt hij alsnog, zoals R-25 vraagt.
      */}
      <div aria-live="polite">{showErrors && touched ? notices : null}</div>
      {showErrors && !touched ? notices : null}

      {presence === undefined || readEmpty ? null : presence(presenceId)}
      {readEmpty ? (
        <p className="ef-veld__leeg" id={fieldId} aria-describedby={description === '' ? undefined : description}>
          {word('nietIngevuld', language)}
        </p>
      ) : (
        children({
          id: fieldId,
          'aria-describedby': description === '' ? undefined : description,
          'aria-invalid': showErrors ? true : undefined,
          'aria-required': required ? true : undefined,
          'aria-labelledby': labelViaHeader ? headerId : undefined,
        })
      )}


      {warnings.length > 0 ? (
        <ul className="ef-veld__meldingen" id={warningId}>
          {warnings.map((finding) => (
            <li key={finding.regelId} className="ef-veld__melding ef-veld__melding--zacht">
              {inLanguage(finding.melding, language)}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
